import * as React from "react"

import{ Link } from "./library/link"

import { Set } from "immutable"

import { NavigationItem } from "../../custom_models/drupal"
import { Container } from "./library/container"
import { Link as CustomLink } from "./library/link"
import { Button } from "./library/button"
import { trackLogin } from "./library/tracking"

import { ApiResult, getNavigation, loadingText, errorText } from "../../custom_api/api"
import { pushMainMenu, pushSecondaryMenu, pushMobileMainMenu, pushMobileSecondaryMenu } from "./datalayer"

interface HeaderProps {}

interface HeaderState {
  readonly items: ApiResult<Set<NavigationItem>>
  readonly secondaryItems: ApiResult<Set<NavigationItem>>
}

const navigationStateZero: HeaderState = { items: "loading", secondaryItems: "loading" }

const mobileMainMenuHandler = (propsHandler: () => void, label: string) => {
  pushMobileMainMenu(label)
  propsHandler()
}

const mobileSecondaryMenuHandler = (propsHandler: () => void, label: string) => {
  pushMobileSecondaryMenu(label)
  propsHandler()
}

const getMenuItemClassName: (title: string) => string = title =>
  title == "Doen"
    ? "activiteiten"
    : title == "Lezen"
    ? "collectie"
    : title == "Leren"
    ? "leren"
    : title == "Word lid" || title == "Lidmaatschap" // FYI: the 'Lidmaatschap' option is the old title; after go-live this could be removed
    ? "lidmaatschap"
    : title == "Bibliotheken" // Changed title so that when "vestigingen en openingstijden" is entered the icon still works
    ? "vestigingen"
    : ""

const loginRoute = "https://probiblio.hostedwise.nl/cgi-bin/bx.pl?event=private;var=portal;vestnr=6190"

const activePath: (path: string) => boolean = path => path == `/${window.location.pathname.split('/')[1]}`;
const getViewport = (): ViewportProps => {
  return window.innerWidth >= 1150 ? 'desktop' : 'mobile';
}

type MenuProps = {
  items: Set<NavigationItem>
  secondaryItems: Set<NavigationItem>
  handle?: () => void
  menuOpen?: boolean
}
type ViewportProps = 'desktop' | 'mobile'

class Menu extends React.Component<MenuProps, { menuOpen: boolean, viewport: ViewportProps }> {
  constructor(props: MenuProps) {
    super(props)
    this.state = {
      menuOpen: false,
      viewport: getViewport()
    }

    this.toggleMenu = this.toggleMenu.bind(this)
    this.handleResize = this.handleResize.bind(this)
  }

  componentDidMount() {
    handleStickyScroll()
    window.addEventListener('scroll', handleStickyScroll)
    window.addEventListener('resize', this.handleResize)
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', handleStickyScroll)
    window.removeEventListener('resize', this.handleResize)
  }

  toggleMenu() {
    this.setState(
      s => ({ ...s, menuOpen: !s.menuOpen }),
      () => (this.state.menuOpen ? document.body.classList.add("menu--toggled") : document.body.classList.remove("menu--toggled"))
    )
  }

  handleResize() {
    const newViewport:ViewportProps = getViewport();
    if(this.state.viewport == newViewport) { return; }

    this.setState(s => ({ ...s, viewport: newViewport }))
  }

  render() {
    const p = this.props

    return (
      <>
        <nav className="nav nav--mobile" aria-label="Primair">
          <Container>
            <Logo key={`logo-${this.state.viewport}`} />
            <Button
              className="nav__toggler"
              handle={this.toggleMenu}
              buttonProps={{
                'aria-controls': 'a11y-nav-collapse',
                'aria-expanded': this.state.menuOpen,
              }}
            >
              Menu <span className="sr-text">{this.state.menuOpen ? 'sluiten': 'openen'}</span>
              <div className="nav__hamburger">
                <span aria-hidden="true"></span>
                <span aria-hidden="true"></span>
                <span aria-hidden="true"></span>
                <span aria-hidden="true"></span>
              </div>
            </Button>
            {this.state.menuOpen ? <MobileMenu items={p.items} secondaryItems={p.secondaryItems} handle={this.toggleMenu} menuOpen={this.state.menuOpen} /> : null}
          </Container>
        </nav>
        <nav id="js--primary-menu" className="nav nav--desktop" aria-label="Primair">
          <DesktopMenu items={p.items} secondaryItems={p.secondaryItems} />
        </nav>
      </>
    )
  }
}

function closeMenuOnBlur(handle:() => void) {
  const focusedMenu:HTMLElement = document.documentElement.querySelector(".menu--mobile:focus-within")
  if(focusedMenu == null) { handle() }
}

const MobileMenu: React.SFC<MenuProps> = props => {
  return <>
    <div
      className="menu menu--mobile"
      id="a11y-nav-collapse"
      aria-hidden={!props.menuOpen}
    >
      <ul className="menu--primary">
        {props.items.map(x => (
          <li key={`li-${x.key}`} className="menu__item">
            {x.external ? (
              <a
                key={x.key}
                className={`menu__link ${activePath(x.relative) ? 'menu__link--active' : ''}`}
                href={x.absolute}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => mobileMainMenuHandler(props.handle, x.title)}
                aria-current={activePath(x.relative) ? 'page' : undefined}
                onBlur={() => closeMenuOnBlur(props.handle)}
              >
                {x.title}
                {getMenuItemClassName(x.title) == 'lidmaatschap' ? (
                  <svg className="menu__icon menu__icon--lidmaatschap" width="2rem" height="2rem" aria-hidden="true"><use xlinkHref="#icon--membership"></use></svg>
                ) : (
                  <span className={`menu__icon menu__icon--${getMenuItemClassName(x.title)}`} aria-hidden="true" />
                )}
              </a>
            ) : (
              <Link
                key={x.key}
                url={x.relative}
                handle={() => mobileMainMenuHandler(props.handle, x.title)}
                className={`menu__link ${activePath(x.relative) ? 'menu__link--active' : ''}`}
                ariaCurrent={activePath(x.relative) ? 'page' : undefined}
                onBlur={() => closeMenuOnBlur(props.handle)}
              >
                {x.title}
                {getMenuItemClassName(x.title) == 'lidmaatschap' ? (
                  <svg className="menu__icon menu__icon--lidmaatschap" width="2rem" height="2rem" aria-hidden="true"><use xlinkHref="#icon--membership"></use></svg>
                ) : (
                  <span className={`menu__icon menu__icon--${getMenuItemClassName(x.title)}`} aria-hidden="true" />
                )}
              </Link>
            )}
          </li>
        ))}
        <li className="menu__item">
          <CustomLink
            url={loginRoute}
            className="link menu__link"
            external={true}
            handle={() => trackLogin()}
            onBlur={() => closeMenuOnBlur(props.handle)}
          >
            Log in
            <svg className="menu__icon menu__icon--login" width="1.6rem" height="2rem" aria-hidden="true"><use xlinkHref="#icon--login"></use></svg>
          </CustomLink>
        </li>
      </ul>
      <ul
        className="menu--secondary"
        id="a11y-nav-collapse"
        aria-hidden={!props.menuOpen}
      >
        {props.secondaryItems.map(x => (
          <li key={`li-${x.key}`} className="menu__item">
            {x.external ? (
              <a
                key={x.key}
                className={`menu__link ${activePath(x.relative) ? 'menu__link--active' : ''}`}
                href={x.absolute}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => mobileSecondaryMenuHandler(props.handle, x.title)}
                aria-current={activePath(x.relative) ? 'page' : undefined}
                onBlur={() => closeMenuOnBlur(props.handle)}
              >
                {x.title}
                <svg className="menu__icon menu__icon--chevron-right" width="5px" height="9px" aria-hidden="true"><use xlinkHref="#icon--chevron-right"></use></svg>
              </a>
            ) : (
              <Link
                key={x.key}
                url={x.relative}
                handle={() => mobileSecondaryMenuHandler(props.handle, x.title)}
                className={`menu__link ${activePath(x.relative) ? 'menu__link--active' : ''}`}
                ariaCurrent={activePath(x.relative) ? 'page' : undefined}
                onBlur={() => closeMenuOnBlur(props.handle)}
              >
                {x.title}
                <svg className="menu__icon menu__icon--chevron-right" width="5px" height="9px" aria-hidden="true"><use xlinkHref="#icon--chevron-right"></use></svg>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  </>
}

const DesktopMenu: React.SFC<MenuProps> = props => {
  return <>
    <div className="menu-container menu-container--primary">
      <Container>
        <ul className="menu menu--desktop menu--primary">
          <li className="menu__link--logo">
            <Logo />
          </li>
          {props.items.map(x => (
            <li key={`li-${x.key}`} className="menu__item">
              {x.external ? (
                <a
                  key={x.key}
                  onClick={() => pushMainMenu(x.title)}
                  className={`menu__link ${activePath(x.relative) ? 'menu__link--active' : ''}`}
                  href={x.absolute}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-current={activePath(x.relative) ? 'page' : undefined}
                >
                  {x.title}
                  {getMenuItemClassName(x.title) == 'lidmaatschap' ? (
                    <svg className="menu__icon menu__icon--lidmaatschap" width="2rem" height="2rem" aria-hidden="true"><use xlinkHref="#icon--membership"></use></svg>
                  ) : (
                    <span className={`menu__icon menu__icon--${getMenuItemClassName(x.title)}`} aria-hidden="true" />
                  )}
                </a>
              ) : (
                <Link
                  key={x.key}
                  handle={() => pushMainMenu(x.title)}
                  url={x.relative}
                  className={`menu__link ${activePath(x.relative) ? 'menu__link--active' : ''}`}
                  ariaCurrent={activePath(x.relative) ? 'page' : undefined}
                >
                  {x.title}
                  {getMenuItemClassName(x.title) == 'lidmaatschap' ? (
                    <svg className="menu__icon menu__icon--lidmaatschap" width="2rem" height="2rem" aria-hidden="true"><use xlinkHref="#icon--membership"></use></svg>
                  ) : (
                    <span className={`menu__icon menu__icon--${getMenuItemClassName(x.title)}`} aria-hidden="true" />
                  )}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </Container>
    </div>
    <div className="menu-container menu-container--secondary">
      <Container>
        <ul className="menu menu--desktop menu--secondary">
          {props.secondaryItems.map(x => (
            <li key={`li-${x.key}`} className="menu__item">
              {x.external ? (
                <a
                  key={x.key}
                  onClick={() => pushSecondaryMenu(x.title)}
                  className={`menu__link ${activePath(x.relative) ? 'menu__link--active' : ''}`}
                  href={x.absolute}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-current={activePath(x.relative) ? 'page' : undefined}
                >
                  {x.title}
                  <svg className="menu__icon menu__icon--chevron-right" width="5px" height="9px" aria-hidden="true"><use xlinkHref="#icon--chevron-right"></use></svg>
                </a>
              ) : (
                <Link
                  key={x.key}
                  handle={() => pushSecondaryMenu(x.title)}
                  url={x.relative}
                  className={`menu__link ${activePath(x.relative) ? 'menu__link--active' : ''}`}
                  ariaCurrent={activePath(x.relative) ? 'page' : undefined}
                >
                  {x.title}
                  <svg className="menu__icon menu__icon--chevron-right" width="5px" height="9px" aria-hidden="true"><use xlinkHref="#icon--chevron-right"></use></svg>
                </Link>
              )}
            </li>
          ))}
          <li>
            <CustomLink url={loginRoute} className="button nav__button" external={true} handle={() => trackLogin()}>
              Log in
            </CustomLink>
          </li>
        </ul>
      </Container>
    </div>
  </>
}

const Logo = () => {
  return (
    <Link url="/" className="logo" title="Ga naar de homepagina" ariaCurrent={window.location.pathname == '/' ? 'page' : undefined}>
      <span className="sr-text">Ga naar de homepagina</span>
      <img src="/bkw/images/aanzet/logo.svg" alt="Bibliotheek AanZet" className="logo--desktop" aria-hidden={window.innerWidth >= 1150 ? undefined : "true"} />
      <img src="/bkw/images/aanzet/logo-mobile.svg" alt="Bibliotheek AanZet" className="logo--mobile" aria-hidden={window.innerWidth >= 1150 ? "true" : undefined} />
    </Link>
  )
}

function handleStickyScroll() {
  const menu:HTMLElement = document.getElementById('js--primary-menu');
  if(menu == null) return;
  if(window.scrollY >= 45) {
    menu.classList.add('sticky');
  } else {
    menu.classList.remove('sticky');
  }
}

export class Header extends React.Component<HeaderProps, HeaderState> {
  constructor(props: HeaderProps) {
    super(props)

    this.state = navigationStateZero
  }

  componentDidMount() {
    const p = this.props

    getNavigation("main").then(res => {
      if (res != "error" && res != "loading") {
        const items = Set(res)

        this.setState(s => ({ ...s, items: items }))
      }
    })

    getNavigation("footer").then(res => {
      if (res != "error" && res != "loading") {
        const items = Set(res)

        this.setState(s => ({ ...s, secondaryItems: items }))
      }
    })
  }

  render() {
    const s = this.state

    const items: 'loading' | 'error' | MenuProps =
      s.items == 'loading' || s.secondaryItems == 'loading' ? 'loading' :
      s.items == 'error'   || s.secondaryItems == 'error'   ? 'error'   :
      { items: s.items, secondaryItems: s.secondaryItems }

    return (
      <>
        <a lang="nl" href="#a11y-main" className="sr-text">Ga direct naar de content</a>
        <a lang="nl"  href="#a11y-footer" className="sr-text">Ga direct naar de footer</a>
        <header lang="nl" className="header">          
          <div className="header__container">
            {
              items == 'loading' ? null :
              items == 'error' ? errorText :
              <Menu {...items} />
            }
          </div>
        </header>
      </>
    )
  }
}
